import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Edit2, XCircle } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import StatusBadge from '../components/StatusBadge';
import LoadingSpinner from '../components/LoadingSpinner';
import { getVehicleDetail, editVehicle, unassignVehicle } from '../api/vehiclesApi';
import { getFullPhotoUrl } from '../utils/imageUrl';
import { useToast } from '../context/ToastContext';

function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewingPhoto, setViewingPhoto] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchVehicle = async () => {
    try {
      const result = await getVehicleDetail(id);
      if (result.success) {
        setVehicle(result.vehicle);
      } else {
        showToast('error', result.message || 'Failed to load vehicle.');
      }
    } catch (err) {
      showToast('error', 'Could not connect to server.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVehicle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Editing/removing is only allowed before pickup — once a driver has
  // physically interacted with the vehicle, its core info is locked.
  const canModify = vehicle && ['awaiting', 'unassigned'].includes(vehicle.vehicle_status);

  const startEdit = () => {
    setEditForm({
      vehicle_name: vehicle.vehicle_name,
      vehicle_model: vehicle.vehicle_model,
      vin_number: vehicle.vin_number,
    });
    setEditMode(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const result = await editVehicle({ id_vehicle: id, ...editForm });
      if (result.success) {
        showToast('success', 'Vehicle updated successfully.');
        setEditMode(false);
        fetchVehicle();
      } else {
        showToast('error', result.message || 'Failed to update vehicle.');
      }
    } catch (err) {
      showToast('error', 'Could not connect to server.');
    } finally {
      setSaving(false);
    }
  };

  const handleUnassign = async () => {
    if (!window.confirm('Remove this vehicle from the load? It will be marked as unassigned rather than deleted.')) return;

    const result = await unassignVehicle(id);
    if (result.success) {
      showToast('success', 'Vehicle unassigned from this load.');
      fetchVehicle();
    } else {
      showToast('error', result.message || 'Failed to unassign vehicle.');
    }
  };

  if (loading || !vehicle) {
    return (
      <div style={{ display: 'flex' }}>
        <Sidebar />
        <div style={{ flex: 1 }}>
          <TopBar title="Loading..." />
          <LoadingSpinner fullScreen />
        </div>
      </div>
    );
  }

  const photos = vehicle.car_photos_url || [];

  return (
    <div style={{ display: 'flex' }}>
      <Sidebar />
      <div style={{ flex: 1 }}>
        <TopBar
          title={`${vehicle.vehicle_name} ${vehicle.vehicle_model}`}
          rightAction={
            canModify && (
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={startEdit} style={styles.editBtn}>
                  <Edit2 size={14} style={{ marginRight: 6 }} /> Edit
                </button>
                <button onClick={handleUnassign} style={styles.unassignBtn}>
                  <XCircle size={14} style={{ marginRight: 6 }} /> Unassign
                </button>
              </div>
            )
          }
        />

        <div style={{ padding: 24, maxWidth: 600 }}>
          {vehicle.vehicle_status === 'unassigned' && (
            <div style={styles.unassignedBanner}>
              This vehicle has been unassigned from its load and is no longer active.
            </div>
          )}

          {!canModify && vehicle.vehicle_status !== 'unassigned' && (
            <div style={styles.lockedBanner}>
              This vehicle has already been picked up and can no longer be edited or removed.
            </div>
          )}

          {editMode ? (
            <form onSubmit={handleSaveEdit} style={styles.card}>
              <label style={styles.label}>Vehicle Make</label>
              <input
                value={editForm.vehicle_name}
                onChange={(e) => setEditForm({ ...editForm, vehicle_name: e.target.value })}
                style={styles.input}
                required
              />
              <label style={styles.label}>Vehicle Model</label>
              <input
                value={editForm.vehicle_model}
                onChange={(e) => setEditForm({ ...editForm, vehicle_model: e.target.value })}
                style={styles.input}
                required
              />
              <label style={styles.label}>VIN Number</label>
              <input
                value={editForm.vin_number}
                onChange={(e) => setEditForm({ ...editForm, vin_number: e.target.value })}
                style={styles.input}
                required
              />
              <div style={styles.buttonRow}>
                <button type="button" onClick={() => setEditMode(false)} style={styles.cancelBtn}>
                  Cancel
                </button>
                <button type="submit" disabled={saving} style={styles.submitBtn}>
                  {saving ? '...' : 'Save Changes'}
                </button>
              </div>
            </form>
          ) : (
            <div style={styles.card}>
              <div style={styles.topRow}>
                <span style={styles.vehicleTitle}>
                  {vehicle.vehicle_name} {vehicle.vehicle_model}
                </span>
                <StatusBadge status={vehicle.vehicle_status} />
              </div>
              <div style={styles.vin}>VIN: {vehicle.vin_number}</div>
              <div style={styles.routeRow}>
                {vehicle.origin} <span style={{ color: '#9ca3af' }}>→</span> {vehicle.destination}
              </div>
            </div>
          )}

          <div style={styles.sectionTitle}>Photos ({photos.length})</div>
          {photos.length === 0 ? (
            <p style={{ color: '#9ca3af' }}>No photos uploaded yet.</p>
          ) : (
            <div style={styles.photoGrid}>
              {photos.map((photo, index) => {
                const fullUrl = getFullPhotoUrl(photo);
                return (
                  <img
                    key={index}
                    src={fullUrl}
                    alt={`Vehicle photo ${index + 1}`}
                    style={styles.photoThumb}
                    onClick={() => setViewingPhoto(fullUrl)}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>

      {viewingPhoto && (
        <div onClick={() => setViewingPhoto(null)} style={styles.photoViewerOverlay}>
          <img src={viewingPhoto} alt="Vehicle full size" style={styles.photoViewerImage} />
        </div>
      )}
    </div>
  );
}

const styles = {
  card: { backgroundColor: 'white', borderRadius: 12, padding: 20, marginBottom: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  unassignedBanner: { backgroundColor: '#fef3c7', color: '#92400e', padding: '10px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, marginBottom: 16 },
  lockedBanner: { backgroundColor: '#eff6ff', color: '#1e40af', padding: '10px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, marginBottom: 16 },
  topRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  vehicleTitle: { fontSize: 16, fontWeight: 700, color: '#111827' },
  vin: { fontSize: 13, color: '#6b7280', marginBottom: 8 },
  routeRow: { fontSize: 13, color: '#374151', fontWeight: 600, borderTop: '1px solid #f3f4f6', paddingTop: 10 },
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6, marginTop: 12 },
  input: { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 14, outline: 'none', boxSizing: 'border-box' },
  buttonRow: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 },
  cancelBtn: { padding: '9px 18px', borderRadius: 8, border: '1px solid #e5e7eb', backgroundColor: 'white', color: '#374151', fontWeight: 600, fontSize: 14, cursor: 'pointer' },
  submitBtn: { padding: '9px 18px', borderRadius: 8, border: 'none', backgroundColor: '#2563eb', color: 'white', fontWeight: 700, fontSize: 14, cursor: 'pointer' },
  editBtn: { display: 'flex', alignItems: 'center', backgroundColor: '#eff6ff', color: '#2563eb', border: 'none', borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  unassignBtn: { display: 'flex', alignItems: 'center', backgroundColor: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  sectionTitle: { fontSize: 14, fontWeight: 700, color: '#374151', marginBottom: 10 },
  photoGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 },
  photoThumb: { width: '100%', aspectRatio: '1', borderRadius: 10, objectFit: 'cover', cursor: 'pointer' },
  photoViewerOverlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20 },
  photoViewerImage: { maxWidth: '100%', maxHeight: '100%', borderRadius: 8, objectFit: 'contain' },
};

export default VehicleDetail;